'use client';

import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { fadeIn } from '@/lib/animations';
import {
	Thermometer,
	Warehouse,
	Globe2,
	Sparkles,
	CheckCircle2,
	ArrowRight,
	BarChart3,
	Shield,
	Clock,
	Package,
} from 'lucide-react';
import { Button } from './components/ui/button';

const capabilities = [
	{
		title: 'Temperature-Controlled Storage',
		description: 'Cold, freezer, and refrigerated solutions designed for sensitive goods.',
		features: [
			'Cold Storage (-10°F to 32°F)',
			'Freezer Storage (-10°F and below)',
			'Refrigerated Storage (32°F to 50°F)',
		],
		icon: Thermometer,
		color: 'bg-blue-50 border-blue-200',
		iconColor: 'text-blue-600',
	},
	{
		title: 'General Warehousing',
		description: 'Secure dry goods storage with optimized racking systems.',
		features: [
			'Climate-Controlled Environment',
			'Advanced Security Systems',
			'Flexible Racking Solutions',
		],
		icon: Warehouse,
		color: 'bg-green-50 border-green-200',
		iconColor: 'text-green-600',
	},
	{
		title: 'Foreign Trade Zone (FTZ)',
		description:
			'Take advantage of customs duty savings and improved import/export flexibility.',
		features: ['Duty Deferral Benefits', 'Import/Export Flexibility', 'Customs Compliance'],
		icon: Globe2,
		color: 'bg-purple-50 border-purple-200',
		iconColor: 'text-purple-600',
	},
	{
		title: 'Value-Added Services',
		description:
			'From kitting and labeling to custom assembly and packaging, we enhance your supply chain.',
		features: [
			'Custom Kitting & Assembly',
			'Labeling & Repackaging',
			'Quality Control Inspections',
		],
		icon: Sparkles,
		color: 'bg-orange-50 border-orange-200',
		iconColor: 'text-orange-600',
	},
	{
		title: 'Inventory Management',
		description: 'Real-time tracking and automated inventory control systems.',
		features: ['WMS Technology', 'Real-Time Visibility', 'Automated Reporting'],
		icon: BarChart3,
		color: 'bg-indigo-50 border-indigo-200',
		iconColor: 'text-indigo-600',
	},
	{
		title: 'Transportation Management',
		description: 'Comprehensive logistics coordination and delivery services.',
		features: ['Route Optimization', 'Fleet Management', 'Last-Mile Delivery'],
		icon: Package,
		color: 'bg-teal-50 border-teal-200',
		iconColor: 'text-teal-600',
	},
];

export function TPLCapabilities() {
	const [ref, inView] = useInView({
		triggerOnce: true,
		threshold: 0,
		rootMargin: '600px 0px',
	});

	return (
		<section className="py-24 bg-gradient-to-b from-gray-50 to-white">
			<div className="container mx-auto px-4 md:px-6">
				<motion.div
					ref={ref}
					initial="hidden"
					animate={inView ? 'show' : 'hidden'}
					variants={fadeIn('up')}
					className="text-center mb-16"
				>
					<h2 className="text-sm font-semibold text-brand-600 uppercase tracking-wider mb-2">
						Our Capabilities
					</h2>
					<h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
						Our 3PL Warehouse Capabilities
					</h3>

					<p className="text-lg text-gray-700 leading-relaxed max-w-3xl mx-auto">
						We take pride in our diverse range of storage options. Whether you need dry
						storage, cold storage, freezer storage, or refrigerated environments, each
						of our four facilities is equipped to handle it. Our WMS (Warehouse
						Management System) technology offers real-time inventory visibility,
						ensuring full control over your stock at all times.
					</p>
				</motion.div>

				<motion.div
					ref={ref}
					initial="hidden"
					animate={inView ? 'show' : 'hidden'}
					variants={fadeIn('up')}
					className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16"
				>
					{capabilities.map((capability, index) => (
						<motion.div
							key={index}
							variants={fadeIn('up', 0.1 * index)}
							className={`${capability.color} border-2 rounded-xl p-8 hover:shadow-lg transition-all duration-300 group`}
						>
							<div
								className={`w-16 h-16 ${capability.color.replace(
									'50',
									'100'
								)} rounded-xl flex items-center justify-center ${
									capability.iconColor
								} mb-6 group-hover:scale-110 transition-transform`}
							>
								<capability.icon size={32} />
							</div>

							<h4 className="text-xl font-bold text-gray-900 mb-3">
								{capability.title}
							</h4>

							<p className="text-gray-700 mb-6 leading-relaxed">
								{capability.description}
							</p>

							<div className="space-y-3">
								{capability.features.map((feature, featureIndex) => (
									<div key={featureIndex} className="flex items-center gap-3">
										<CheckCircle2 className={capability.iconColor} size={16} />
										<span className="text-sm text-gray-600">{feature}</span>
									</div>
								))}
							</div>
						</motion.div>
					))}
				</motion.div>
			</div>
		</section>
	);
}
